import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import type { z } from 'zod';

import {
  UpdateWorkflowTemplateParamsSchema,
  WorkflowTemplateFormSchema,
  WorkflowTemplateModel,
  WorkflowTemplatesModel,
} from '../models/template';
import { queryKeyMap } from '../utils/query-keys';

const API_BASE_URL = '/api/workflows/templates/';

export const useWorkflowTemplates = <
  T = z.infer<typeof WorkflowTemplatesModel>,
>(
  select?: (data: z.infer<typeof WorkflowTemplatesModel>) => T,
) => {
  return useQuery({
    queryKey: queryKeyMap.templates(),
    queryFn: async () => {
      const res = await axiosGet(API_BASE_URL);
      return WorkflowTemplatesModel.parse(res);
    },
    select,
  });
};

const _useWorkflowTemplate = (id: string) => {
  return useQuery({
    queryKey: queryKeyMap.template(id),
    queryFn: async () => {
      const res = await axiosGet(`${API_BASE_URL}${id}/`);
      return WorkflowTemplateModel.parse(res);
    },
    enabled: !!id,
  });
};

const _useCreateWorkflowTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof WorkflowTemplateFormSchema>) => {
      const { name, ...data } = WorkflowTemplateFormSchema.parse(values);
      return await axiosPost(API_BASE_URL, {
        name,
        data,
      });
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.templates());
    },
  });
};

const _useUpdateWorkflowTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof UpdateWorkflowTemplateParamsSchema>,
    ) => {
      const { id, name, ...data } =
        UpdateWorkflowTemplateParamsSchema.parse(values);
      return await axiosPatch(`${API_BASE_URL}${id}/`, {
        name,
        data,
      });
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        invalidate(queryKeyMap.templates()),
        invalidate(queryKeyMap.template(id)),
      ]);
    },
  });
};

const _useDeleteWorkflowTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();

  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`${API_BASE_URL}${id}/`);
    },
    onSuccess: async (_, id) => {
      await invalidate(queryKeyMap.templates());

      removeQuery(queryKeyMap.template(id));
    },
  });
};
